import { useMemo } from "react";

export interface SearchCompletionParams {
    loading: boolean;
    initialised: boolean;
    filtersLoading: boolean;
    error?: Error | null;
    rowsLoaded: number;
    itemsPerPage: number;
    meta: {
        page?: number;
        pages?: number;
        skip?: number;
        total?: number;
    };
}

/**
 * Determines if a WebSocket-based table search has enough data to enable the toolbar.
 *
 * The server streams results in batches (e.g. 300 rows per `search:row` event).
 * With large page sizes (e.g. 5000), waiting for ALL rows blocks the UI for seconds.
 * Instead, we consider the search "complete" as soon as the first batch arrives
 * and `loading` is set to false — allowing users to interact with visible data
 * while remaining rows continue streaming in.
 */
export function isSearchComplete(params: SearchCompletionParams): boolean {
    const { loading, initialised, filtersLoading, error, rowsLoaded, meta } = params;

    // Table not ready or filters still resolving
    if (!initialised || filtersLoading || !!error) {
        return false;
    }

    // Initial loading state — no data has arrived yet
    if (loading && rowsLoaded === 0) {
        return false;
    }

    // First batch of rows has arrived and loading flag cleared — toolbar can enable
    if (!loading && rowsLoaded > 0) {
        return true;
    }

    // Empty result set (meta arrived with total: 0, no rows expected)
    if (meta.total === 0 && rowsLoaded === 0 && !loading) {
        return true;
    }

    return false;
}

/**
 * Hook wrapper around isSearchComplete.
 * Returns `true` when the toolbar should be disabled (search not yet complete).
 */
export function useSearchCompletion(params: SearchCompletionParams): boolean {
    return useMemo(() => !isSearchComplete(params), [
        params.loading,
        params.initialised,
        params.filtersLoading,
        params.error,
        params.rowsLoaded,
        params.itemsPerPage,
        params.meta?.page,
        params.meta?.pages,
        params.meta?.skip,
        params.meta?.total,
    ]);
}
